import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  getCourses,
  getAdminDashboardMetrics,
  getLearnersPaginated,
  getEnrolmentsByLga,
  getChartQuerySchema,
  runChartQuery,
} from "../api";
import "./Analytics.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const Analytics = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const isCustomChartsComingSoon = true;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [learners, setLearners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedLgaCourse, setSelectedLgaCourse] = useState("");
  const [querySchema, setQuerySchema] = useState(null);
  const [querySchemaLoading, setQuerySchemaLoading] = useState(false);
  const [customQueryLoading, setCustomQueryLoading] = useState(false);
  const [customQueryError, setCustomQueryError] = useState("");
  const [customQueryResult, setCustomQueryResult] = useState(null);
  const [queryConfig, setQueryConfig] = useState({
    chartType: "bar",
    sourceTable: "",
    xAxisTable: "",
    xAxisColumn: "",
    metricFunc: "count",
    metricTable: "",
    metricColumn: "",
    limit: 20,
    joins: [],
  });

  // M&E Data - calculated from real backend data
  const [meData, setMeData] = useState({
    totalEnrolledUsers: 0,
    dailyActiveUsers: 0,
    monthlyActiveUsers: 0,
    activeUsers: 0, // Keeping for backward compat if needed
    inactiveUsers: 0,
    courseEnrollments: [],
    newRegistrations: [],
    newEnrollments: [],
    demographics: {
      female: 0,
      male: 0,
    },
    cadreData: [],
  });

  // Fetch all data
  const fetchData = useCallback(
    async (showRefreshing = false) => {
      try {
        if (showRefreshing) setRefreshing(true);
        else setLoading(true);

        // Fetch main data in parallel for faster loading
        const [coursesRes, metricsRes, enrolmentsByLgaRes] = await Promise.all([
          getCourses().catch(() => []),
          getAdminDashboardMetrics().catch(() => null),
          getEnrolmentsByLga(
            selectedLgaCourse ? { course_id: selectedLgaCourse } : {},
          ).catch(() => null),
        ]);

        // Keep this lightweight on initial load. Fetch a smaller learner sample only
        // when needed for demographics/cadre distributions.
        const learnersSampleRes = await getLearnersPaginated(1, 200).catch(
          () => ({ data: [] }),
        );

        const learnersSample = learnersSampleRes?.data || [];

        setCourses(coursesRes || []);
        setLearners(learnersSample);

        // Use metrics response as the single source of truth
        const dashboardData = metricsRes || {};
        setDashboardData(dashboardData);

        // Set loading to false after initial data is loaded
        if (showRefreshing) setRefreshing(false);
        else setLoading(false);

        // Use learnersSample (1000 records) for better distribution data than top_staff (10 records)
        const learnersWithLocation =
          learnersSample.length > 0
            ? learnersSample
            : dashboardData.top_staff || [];

        // Calculate real M&E data from backend metrics
        // If total_learners is not in metrics, use length of top_staff or enrollments as proxy
        const totalEnrolledUsers =
          dashboardData.total_learners || learnersWithLocation.length || 0;

        const activeMonth = dashboardData.mau_active || 0;

        // Use direct DAU/MAU from backend
        const activeUsers = dashboardData.dau_active || 0;
        const suspendedUsers = 0; // Not provided in new metrics yet
        const inactiveUsers = Math.max(0, totalEnrolledUsers - activeMonth);

        // Calculate course enrollments with real data
        const courseEnrollments = (coursesRes || []).map((course) => {
          const enrolled = course.learners_count || 0;
          const completed = course.completed || 0;
          const completionRate =
            enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;

          return {
            name: course.title || "Untitled Course",
            enrolled: enrolled,
            hoursSpent: Math.round(enrolled * 2.5),
            assessmentsCompleted: Math.round(enrolled * 5),
            completionRate: completionRate,
          };
        });

        // New Registrations - fallback to empty if not in metrics
        const newRegistrations = dashboardData.new_registrations || [];

        // New Enrollments - estimate from total
        const newEnrollments = dashboardData.new_enrollments || []; // simplified

        // Calculate demographics from top_staff sample
        const femaleCount = learnersWithLocation.filter((l) => {
          const gender = (l.gender || "").toLowerCase();
          return gender === "female" || gender === "f";
        }).length;
        const maleCount = learnersWithLocation.filter((l) => {
          const gender = (l.gender || "").toLowerCase();
          return gender === "male" || gender === "m";
        }).length;

        // Calculate users by cadre (job_title) using top_staff sample
        const cadreCounts = {};
        learnersWithLocation.forEach((learner) => {
          let cadre = "Unknown";

          // Try cadre first (as in UserManagement)
          if (learner.cadre) {
            if (typeof learner.cadre === "object" && learner.cadre !== null) {
              cadre = learner.cadre.name || learner.cadre.title || "Unknown";
            } else if (
              typeof learner.cadre === "string" &&
              learner.cadre.trim() !== ""
            ) {
              cadre = learner.cadre.trim();
            }
          }
          // Fallback to job_title if cadre not found
          if (cadre === "Unknown" && learner.job_title) {
            if (
              typeof learner.job_title === "object" &&
              learner.job_title !== null
            ) {
              cadre =
                learner.job_title.title || learner.job_title.name || "Unknown";
            } else if (
              typeof learner.job_title === "string" &&
              learner.job_title.trim() !== ""
            ) {
              cadre = learner.job_title.trim();
            }
          }
          // Fallback to job_title_name
          if (cadre === "Unknown" && learner.job_title_name) {
            cadre = learner.job_title_name.trim();
          }
          // Fallback to jobtitle (alternative field name)
          if (cadre === "Unknown" && learner.jobtitle) {
            if (
              typeof learner.jobtitle === "object" &&
              learner.jobtitle !== null
            ) {
              cadre =
                learner.jobtitle.name || learner.jobtitle.title || "Unknown";
            } else if (
              typeof learner.jobtitle === "string" &&
              learner.jobtitle.trim() !== ""
            ) {
              cadre = learner.jobtitle.trim();
            }
          }
          cadreCounts[cadre] = (cadreCounts[cadre] || 0) + 1;
        });
        const cadreData = Object.entries(cadreCounts)
          .map(([name, value]) => ({
            name,
            value,
          }))
          .filter((item) => item.value > 0 && item.name !== "Unknown") // Only include cadres with users and exclude Unknown
          .sort((a, b) => b.value - a.value);
        const resolveLgaName = (entity = {}) => {
          if (entity.lga && typeof entity.lga === "object") {
            return (
              entity.lga.name ||
              entity.lga.title ||
              entity.lga.lga_name ||
              "N/A"
            );
          }
          if (typeof entity.lga === "string" && entity.lga.trim()) {
            return entity.lga.trim();
          }
          if (
            entity.lga_id &&
            entity.state?.lgas &&
            Array.isArray(entity.state.lgas)
          ) {
            const found = entity.state.lgas.find(
              (lga) => lga.id === entity.lga_id,
            );
            if (found?.name) return found.name;
          }
          if (
            entity.lga_name &&
            entity.lga_name.trim() &&
            entity.lga_name !== "N/A"
          ) {
            return entity.lga_name.trim();
          }
          if (entity.lga_id) {
            return `LGA #${entity.lga_id}`;
          }
          return "N/A";
        };

        // Calculate users per LGA
        const lgaCounts = {};
        learnersWithLocation.forEach((learner) => {
          const lgaName = resolveLgaName(learner);
          if (lgaName && lgaName !== "N/A") {
            lgaCounts[lgaName] = (lgaCounts[lgaName] || 0) + 1;
          }
        });
        const lgaData = Object.entries(lgaCounts)
          .map(([name, value]) => ({
            name: name.length > 20 ? name.substring(0, 20) + "..." : name,
            fullName: name,
            value,
          }))
          .filter((item) => item.value > 0)
          .sort((a, b) => b.value - a.value)
          .slice(0, 15); // Top 15 LGAs

        const enrolmentLgaData = Array.isArray(enrolmentsByLgaRes?.data)
          ? enrolmentsByLgaRes.data
              .map((item) => ({
                name:
                  (item.lga_name || "Unknown").length > 20
                    ? (item.lga_name || "Unknown").substring(0, 20) + "..."
                    : item.lga_name || "Unknown",
                fullName: item.lga_name || "Unknown",
                value: Number(item.enrolments_count || 0),
              }))
              .filter((item) => item.value > 0)
              .sort((a, b) => b.value - a.value)
              .slice(0, 15)
          : [];

        setMeData({
          totalEnrolledUsers,
          activeUsers, // kept for reference/calculation
          inactiveUsers,
          suspendedUsers,
          dailyActiveUsers: metricsRes?.dau_active || 0,
          monthlyActiveUsers: metricsRes?.mau_active || 0,
          courseEnrollments,
          newRegistrations,
          newEnrollments,
          demographics: {
            female: femaleCount,
            male: maleCount,
          },
          cadreData,
          lgaData: enrolmentLgaData.length > 0 ? enrolmentLgaData : lgaData,
        });

        setLastUpdated(new Date());
      } catch (err) {
        // Error handled silently
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedLgaCourse],
  );

  useEffect(() => {
    fetchData();

    // Auto-refresh every 2 minutes (reduced from 30s to prevent rate limiting)
    const interval = setInterval(() => {
      fetchData(true);
    }, 120000);

    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const loadChartSchema = async () => {
      if (isCustomChartsComingSoon) return;

      setQuerySchemaLoading(true);
      try {
        const schema = await getChartQuerySchema();
        setQuerySchema(schema);

        const defaultTable = Object.keys(schema?.tables || {})[0] || "";
        const defaultColumns = schema?.tables?.[defaultTable] || [];

        setQueryConfig((prev) => ({
          ...prev,
          sourceTable: defaultTable,
          xAxisTable: defaultTable,
          xAxisColumn: defaultColumns[0] || "",
          metricTable: defaultTable,
          metricColumn: defaultColumns[0] || "",
          metricFunc: (schema?.aggregates || ["count"])[0] || "count",
        }));
      } catch (_err) {
        setCustomQueryError("Could not load query schema.");
      } finally {
        setQuerySchemaLoading(false);
      }
    };

    loadChartSchema();
  }, [isCustomChartsComingSoon]);

  // Calculate metrics from dashboard data
  const metrics = dashboardData
    ? {
        totalEnrollments: dashboardData.enrollments || 0,
        completedCourses: dashboardData.completed_courses || 0,
        averageScore: dashboardData.average_score
          ? parseFloat(dashboardData.average_score).toFixed(1)
          : "0.0",
        totalCourses: courses.length,
        totalLearners: (dashboardData?.top_staff || []).length,
        publishedCourses: courses.filter(
          (c) => c.is_published && !c.is_archived,
        ).length,
        activeEnrollments:
          dashboardData.enrollments - (dashboardData.completed_courses || 0),
      }
    : {};

  // M&E Metrics - Auto-calculated percentages (recalculates when meData changes)
  const meMetrics = {
    activePercentage:
      meData.totalEnrolledUsers > 0
        ? ((meData.activeUsers / meData.totalEnrolledUsers) * 100).toFixed(1)
        : 0,
    inactivePercentage:
      meData.totalEnrolledUsers > 0
        ? ((meData.inactiveUsers / meData.totalEnrolledUsers) * 100).toFixed(1)
        : 0,
    suspendedPercentage:
      meData.totalEnrolledUsers > 0
        ? ((meData.suspendedUsers / meData.totalEnrolledUsers) * 100).toFixed(1)
        : 0,
    totalCourseEnrollments: meData.courseEnrollments.reduce(
      (sum, course) => sum + course.enrolled,
      0,
    ),
    totalDemographics: meData.demographics.female + meData.demographics.male,
    femalePercentage:
      meData.demographics.female + meData.demographics.male > 0
        ? (
            (meData.demographics.female /
              (meData.demographics.female + meData.demographics.male)) *
            100
          ).toFixed(1)
        : 0,
    malePercentage:
      meData.demographics.female + meData.demographics.male > 0
        ? (
            (meData.demographics.male /
              (meData.demographics.female + meData.demographics.male)) *
            100
          ).toFixed(1)
        : 0,
  };

  const getColumnsForTable = (table) => querySchema?.tables?.[table] || [];
  const getJoinTargets = (table) =>
    table ? Object.keys(querySchema?.joins?.[table] || {}) : [];

  const handleSourceTableChange = (value) => {
    const sourceColumns = getColumnsForTable(value);
    setQueryConfig((prev) => ({
      ...prev,
      sourceTable: value,
      xAxisTable: value,
      xAxisColumn: sourceColumns[0] || "",
      metricTable: value,
      metricColumn: sourceColumns[0] || "",
      joins: [],
    }));
  };

  const handleXAxisTableChange = (value) => {
    const columns = getColumnsForTable(value);
    setQueryConfig((prev) => ({
      ...prev,
      xAxisTable: value,
      xAxisColumn: columns[0] || "",
    }));
  };

  const handleMetricTableChange = (value) => {
    const columns = getColumnsForTable(value);
    setQueryConfig((prev) => ({
      ...prev,
      metricTable: value,
      metricColumn: columns[0] || "",
    }));
  };

  const updateJoinRow = (index, key, value) => {
    setQueryConfig((prev) => {
      const joins = [...prev.joins];
      const current = joins[index] || { fromTable: prev.sourceTable, toTable: "" };
      joins[index] = {
        ...current,
        [key]: value,
      };

      if (key === "fromTable") {
        const firstTarget = getJoinTargets(value)[0] || "";
        joins[index].toTable = firstTarget;
      }

      return { ...prev, joins };
    });
  };

  const addJoinRow = () => {
    setQueryConfig((prev) => ({
      ...prev,
      joins: [
        ...prev.joins,
        {
          fromTable: prev.sourceTable,
          toTable: getJoinTargets(prev.sourceTable)[0] || "",
        },
      ],
    }));
  };

  const removeJoinRow = (index) => {
    setQueryConfig((prev) => ({
      ...prev,
      joins: prev.joins.filter((_, i) => i !== index),
    }));
  };

  const loadGenderExample = () => {
    if (!querySchema?.tables?.staff) return;
    setCustomQueryResult(null);
    setCustomQueryError("");
    setQueryConfig((prev) => ({
      ...prev,
      chartType: "bar",
      sourceTable: "staff",
      xAxisTable: "staff",
      xAxisColumn: "gender",
      metricFunc: "count",
      metricTable: "staff",
      metricColumn: "id",
      joins: [],
      limit: 20,
    }));
  };

  const getApiErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) return "Query failed. Please try again.";
    if (data.message) return data.message;

    const firstError = data.errors
      ? Object.values(data.errors).flat()[0]
      : null;
    return firstError || "Query failed. Please try again.";
  };

  const handleRunCustomQuery = async () => {
    setCustomQueryLoading(true);
    setCustomQueryError("");

    const validJoins = (queryConfig.joins || []).filter(
      (j) => j.fromTable && j.toTable,
    );

    const payload = {
      chartType: queryConfig.chartType,
      sourceTable: queryConfig.sourceTable,
      xAxis: {
        table: queryConfig.xAxisTable,
        column: queryConfig.xAxisColumn,
      },
      metric: {
        func: queryConfig.metricFunc,
        table: queryConfig.metricTable,
        column: queryConfig.metricColumn,
        alias: "value",
      },
      groupBy: [
        {
          table: queryConfig.xAxisTable,
          column: queryConfig.xAxisColumn,
        },
      ],
      orderBy: {
        column: "value",
        direction: "desc",
      },
      limit: Number(queryConfig.limit) || 20,
    };

    if (validJoins.length > 0) {
      payload.joins = validJoins;
    }

    try {
      const response = await runChartQuery(payload);
      setCustomQueryResult(response);
    } catch (err) {
      setCustomQueryError(getApiErrorMessage(err));
      setCustomQueryResult(null);
    } finally {
      setCustomQueryLoading(false);
    }
  };

  // Top courses chart data
  const topCoursesData = dashboardData?.top_courses
    ? {
        labels: dashboardData.top_courses
          .slice(0, 8)
          .map((c) => c.title || "Untitled"),
        datasets: [
          {
            label: "Enrollments",
            data: dashboardData.top_courses
              .slice(0, 8)
              .map((c) => c.learners_count || 0),
            backgroundColor: "rgba(247, 118, 0, 0.8)",
            borderColor: "rgba(247, 118, 0, 1)",
            borderWidth: 2,
            borderRadius: 8,
          },
        ],
      }
    : null;

  // Course status chart (completed vs pending)
  const courseStatusData = dashboardData?.course_status
    ? {
        labels: dashboardData.course_status
          .slice(0, 6)
          .map((c) => c.title || "Untitled"),
        datasets: [
          {
            label: "Completed",
            data: dashboardData.course_status
              .slice(0, 6)
              .map((c) => c.completed || 0),
            backgroundColor: "rgba(40, 167, 69, 0.8)",
            borderColor: "rgba(40, 167, 69, 1)",
            borderWidth: 2,
          },
          {
            label: "Pending",
            data: dashboardData.course_status
              .slice(0, 6)
              .map((c) => c.pending || 0),
            backgroundColor: "rgba(247, 118, 0, 0.8)",
            borderColor: "rgba(247, 118, 0, 1)",
            borderWidth: 2,
          },
        ],
      }
    : null;

  // Top staff performance chart
  const topStaffData = dashboardData?.top_staff
    ? {
        labels: dashboardData.top_staff
          .slice(0, 8)
          .map(
            (s) =>
              s.full_name ||
              `${s.firstname || ""} ${s.lastname || ""}`.trim() ||
              "Unknown",
          ),
        datasets: [
          {
            label: "Average Score (%)",
            data: dashboardData.top_staff
              .slice(0, 8)
              .map((s) => parseFloat(s.tests_avg_percentile || 0).toFixed(1)),
            borderColor: "rgba(247, 118, 0, 1)",
            backgroundColor: "rgba(247, 118, 0, 0.1)",
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: "rgba(247, 118, 0, 1)",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
          },
        ],
      }
    : null;

  // Enrollment distribution (Doughnut)
  const enrollmentDistribution = dashboardData
    ? {
        labels: ["Completed", "In Progress"],
        datasets: [
          {
            data: [
              dashboardData.completed_courses || 0,
              (dashboardData.enrollments || 0) -
                (dashboardData.completed_courses || 0),
            ],
            backgroundColor: [
              "rgba(40, 167, 69, 0.8)",
              "rgba(247, 118, 0, 0.8)",
            ],
            borderColor: ["rgba(40, 167, 69, 1)", "rgba(247, 118, 0, 1)"],
            borderWidth: 2,
          },
        ],
      }
    : null;

  // Active/Inactive/Suspended Users Chart
  const activeInactiveData = {
    labels: ["Active Users", "Inactive Users", "Suspended Users"],
    datasets: [
      {
        data: [
          meData.activeUsers,
          meData.inactiveUsers,
          meData.suspendedUsers || 0,
        ],
        backgroundColor: [
          "rgba(40, 167, 69, 0.8)",
          "rgba(255, 193, 7, 0.8)",
          "rgba(220, 53, 69, 0.8)",
        ],
        borderColor: [
          "rgba(40, 167, 69, 1)",
          "rgba(255, 193, 7, 1)",
          "rgba(220, 53, 69, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  // Demographics Chart
  const demographicsData = {
    labels: ["Female", "Male"],
    datasets: [
      {
        data: [meData.demographics.female, meData.demographics.male],
        backgroundColor: ["rgba(255, 99, 132, 0.8)", "rgba(54, 162, 235, 0.8)"],
        borderColor: ["rgba(255, 99, 132, 1)", "rgba(54, 162, 235, 1)"],
        borderWidth: 2,
      },
    ],
  };

  // Cadre Chart
  const cadreChartData = {
    labels: (meData.cadreData || []).map((c) => c.name),
    datasets: [
      {
        data: (meData.cadreData || []).map((c) => c.value),
        backgroundColor: [
          "rgba(16, 185, 129, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(139, 92, 246, 0.8)",
          "rgba(236, 72, 153, 0.8)",
          "rgba(6, 182, 212, 0.8)",
          "rgba(132, 204, 22, 0.8)",
          "rgba(249, 115, 22, 0.8)",
          "rgba(99, 102, 241, 0.8)",
          "rgba(20, 184, 166, 0.8)",
          "rgba(168, 85, 247, 0.8)",
          "rgba(244, 63, 94, 0.8)",
          "rgba(251, 146, 60, 0.8)",
        ],
        borderColor: [
          "rgba(16, 185, 129, 1)",
          "rgba(59, 130, 246, 1)",
          "rgba(245, 158, 11, 1)",
          "rgba(239, 68, 68, 1)",
          "rgba(139, 92, 246, 1)",
          "rgba(236, 72, 153, 1)",
          "rgba(6, 182, 212, 1)",
          "rgba(132, 204, 22, 1)",
          "rgba(249, 115, 22, 1)",
          "rgba(99, 102, 241, 1)",
          "rgba(20, 184, 166, 1)",
          "rgba(168, 85, 247, 1)",
          "rgba(244, 63, 94, 1)",
          "rgba(251, 146, 60, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  // LGA Chart
  const lgaChartData = {
    labels: (meData.lgaData || []).map((l) => l.name),
    datasets: [
      {
        label: "Enrolments",
        data: (meData.lgaData || []).map((l) => l.value),
        backgroundColor: (meData.lgaData || []).map(
          (_, index) => `hsl(${200 + index * 10}, 70%, 50%)`,
        ),
        borderColor: (meData.lgaData || []).map(
          (_, index) => `hsl(${200 + index * 10}, 70%, 40%)`,
        ),
        borderWidth: 2,
      },
    ],
  };

  const customChartData = customQueryResult
    ? {
        labels: customQueryResult.labels || [],
        datasets: (customQueryResult.datasets || []).map((set, idx) => ({
          label: set.name || "Values",
          data: set.data || [],
          backgroundColor:
            queryConfig.chartType === "pie"
              ? (set.data || []).map((_, i) => `hsl(${(i * 37) % 360}, 68%, 55%)`)
              : `hsl(${(idx * 37) % 360}, 72%, 52%)`,
          borderColor:
            queryConfig.chartType === "pie"
              ? (set.data || []).map((_, i) => `hsl(${(i * 37) % 360}, 68%, 42%)`)
              : `hsl(${(idx * 37) % 360}, 72%, 40%)`,
          borderWidth: 2,
        })),
      }
    : null;

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "600",
        },
        bodyFont: {
          size: 13,
        },
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        cornerRadius: 8,
      },
    },
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const formatTime = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="metric-card skeleton-card">
      <div className="skeleton-line skeleton-title"></div>
      <div className="skeleton-line skeleton-value"></div>
      <div className="skeleton-line skeleton-subtitle"></div>
    </div>
  );

  const SkeletonChart = () => (
    <div className="chart-card skeleton-chart">
      <div className="skeleton-line skeleton-chart-title"></div>
      <div className="skeleton-chart-content"></div>
    </div>
  );

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-header">
          <div className="header-top">
            <div className="skeleton-line skeleton-header-title"></div>
            <div className="skeleton-line skeleton-header-subtitle"></div>
          </div>
        </div>
        <div className="metrics-grid">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="charts-grid">
          {[...Array(4)].map((_, i) => (
            <SkeletonChart key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="header-top">
          <h1>Analytics Dashboard</h1>
          <div className="header-actions">
            <button
              className={`refresh-btn ${refreshing ? "refreshing" : ""}`}
              onClick={handleRefresh}
              title="Refresh data"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
              </svg>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            {lastUpdated && (
              <div className="last-updated">
                <span className="live-dot"></span>
                Last updated: {formatTime(lastUpdated)}
              </div>
            )}
          </div>
        </div>
        <div className="analytics-tabs">
          {["overview", "courses", "learners", "custom"].map((tab) => (
            (() => {
              const isDisabled = tab === "custom" && isCustomChartsComingSoon;

              return (
            <button
              key={tab}
              className={`tab-button ${activeTab === tab && !isDisabled ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
              onClick={() => !isDisabled && setActiveTab(tab)}
              disabled={isDisabled}
              aria-disabled={isDisabled}
            >
              <span>
                {tab === "overview"
                  ? "Overview"
                  : tab === "courses"
                    ? "Courses"
                    : tab === "learners"
                      ? "Learners"
                      : "Custom Charts"}
              </span>
              {isDisabled && <span className="tab-tag">Coming soon</span>}
            </button>
              );
            })()
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="analytics-content">
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
            {/* Total Users */}
            <div className="metric-card primary">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Total FLWs</h3>
                <div className="metric-value">
                  {meData.totalEnrolledUsers || 0}
                </div>
                <div className="metric-label">All registered FLWs</div>
              </div>
            </div>

            {/* Daily Active Users */}
            <div className="metric-card success">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Daily Active Users</h3>
                <div className="metric-value">
                  {meData.dailyActiveUsers || 0}
                </div>
                <div className="metric-label">Last 24 hours</div>
              </div>
            </div>

            {/* Monthly Active Users */}
            <div className="metric-card warning">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Monthly Active Users</h3>
                <div className="metric-value">
                  {meData.monthlyActiveUsers || 0}
                </div>
                <div className="metric-label">Last 30 days</div>
              </div>
            </div>

            {/* Total Enrollments */}
            <div className="metric-card info">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Total Enrollments</h3>
                <div className="metric-value">
                  {metrics.totalEnrollments || 0}
                </div>
                <div className="metric-label">All course enrollments</div>
              </div>
            </div>

            {/* Completed Courses */}
            <div className="metric-card success">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Completed Enrollments</h3>
                <div className="metric-value">
                  {metrics.completedCourses || 0}
                </div>
                <div className="metric-label">
                  Out of {metrics.totalEnrollments || 0} enrollments
                </div>
              </div>
            </div>

            <div className="metric-card warning">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Total Courses</h3>
                <div className="metric-value">{metrics.totalCourses || 0}</div>
                <div className="metric-label">
                  {metrics.publishedCourses || 0} published
                </div>
              </div>
            </div>

            <div className="metric-card info">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Average Score</h3>
                <div className="metric-value">
                  {metrics.averageScore || "0.0"}%
                </div>
                <div className="metric-label">Across all tests</div>
              </div>
            </div>

            {/* Inactive Users */}
            <div className="metric-card warning">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Inactive Users (Current Month)</h3>
                <div className="metric-value">{meData.inactiveUsers || 0}</div>
                <div className="metric-label">
                  Inactive: {meMetrics.inactivePercentage}%
                </div>
              </div>
            </div>

            {/* Suspended Users */}
            <div className="metric-card danger">
              <div className="metric-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <div className="metric-content">
                <h3>Suspended Users</h3>
                <div className="metric-value">{meData.suspendedUsers || 0}</div>
                <div className="metric-label">
                  Suspended: {meMetrics.suspendedPercentage}%
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Top Courses by Enrollments</h3>
                <p>Most popular courses</p>
              </div>
              <div className="chart-container">
                {topCoursesData ? (
                  <Bar data={topCoursesData} options={chartOptions} />
                ) : (
                  <div className="chart-placeholder">No data available</div>
                )}
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Top Performers</h3>
                <p>Best scoring learners</p>
              </div>
              <div className="chart-container">
                {topStaffData ? (
                  <Line data={topStaffData} options={chartOptions} />
                ) : (
                  <div className="chart-placeholder">No data available</div>
                )}
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Enrollment Status</h3>
                <p>Completion distribution</p>
              </div>
              <div className="chart-container">
                {enrollmentDistribution ? (
                  <Doughnut
                    data={enrollmentDistribution}
                    options={doughnutOptions}
                  />
                ) : (
                  <div className="chart-placeholder">No data available</div>
                )}
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Course Completion Status</h3>
                <p>Completed vs Pending</p>
              </div>
              <div className="chart-container">
                {courseStatusData ? (
                  <Bar data={courseStatusData} options={chartOptions} />
                ) : (
                  <div className="chart-placeholder">No data available</div>
                )}
              </div>
            </div>

            {/* <div className="chart-card">
              <div className="chart-header">
                <h3>New Registrations</h3>
                <p>Monthly user growth</p>
              </div>
              <div className="chart-container">
                <Line data={newRegistrationsData} options={chartOptions} />
              </div>
            </div> */}

            {/* <div className="chart-card">
              <div className="chart-header">
                <h3>New Enrollments</h3>
                <p>Monthly enrollment growth</p>
              </div>
              <div className="chart-container">
                <Line data={newEnrollmentsData} options={chartOptions} />
              </div>
            </div> */}

            <div className="chart-card">
              <div className="chart-header">
                <h3>User Activity Distribution</h3>
                <p>Active vs Inactive vs Suspended Users</p>
              </div>
              <div className="chart-container">
                <Doughnut data={activeInactiveData} options={doughnutOptions} />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Demographics</h3>
                <p>
                  Gender distribution - Female: {meData.demographics.female} (
                  {meMetrics.femalePercentage}%), Male:{" "}
                  {meData.demographics.male} ({meMetrics.malePercentage}%)
                </p>
              </div>
              <div className="chart-container">
                <Doughnut data={demographicsData} options={doughnutOptions} />
              </div>
            </div>

            <div className="chart-card" style={{ gridColumn: "span 2" }}>
              <div className="chart-header">
                <h3>Users by Cadre</h3>
                <p>
                  Distribution of users by job title/cadre - Total:{" "}
                  {(meData.cadreData || []).reduce(
                    (sum, c) => sum + c.value,
                    0,
                  )}{" "}
                  users
                </p>
              </div>
              <div className="chart-container">
                {meData.cadreData && meData.cadreData.length > 0 ? (
                  <Doughnut data={cadreChartData} options={doughnutOptions} />
                ) : (
                  <div className="chart-placeholder">
                    No cadre data available
                  </div>
                )}
              </div>
            </div>

            <div className="chart-card" style={{ gridColumn: "span 2" }}>
              <div className="chart-header">
                <h3>Enrolments by LGA</h3>
                <p>
                  Distribution of enrolments by Local Government Area (Top 15) -
                  Total:{" "}
                  {(meData.lgaData || []).reduce((sum, l) => sum + l.value, 0)}{" "}
                  enrolments
                </p>
                <div style={{ marginTop: "10px" }}>
                  <select
                    value={selectedLgaCourse}
                    onChange={(e) => setSelectedLgaCourse(e.target.value)}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      background: "#fff",
                      color: "#333",
                    }}
                  >
                    <option value="">All courses</option>
                    {(courses || []).map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title || `Course #${course.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="chart-container">
                {meData.lgaData && meData.lgaData.length > 0 ? (
                  <Bar
                    data={lgaChartData}
                    options={{
                      ...chartOptions,
                      indexAxis: "y",
                      plugins: {
                        ...chartOptions.plugins,
                        tooltip: {
                          ...chartOptions.plugins.tooltip,
                          callbacks: {
                            title: (items) => {
                              const index = items[0].dataIndex;
                              return (
                                meData.lgaData[index]?.fullName ||
                                items[0].label
                              );
                            },
                            label: (context) =>
                              `Enrolments: ${context.parsed.x}`,
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="chart-placeholder">No LGA data available</div>
                )}
              </div>
            </div>
          </div>

          {/* Top Learners */}
          <div className="tables-grid">
            <div className="table-card">
              <div className="table-header">
                <h3>Top Performing Learners</h3>
                <span className="badge">
                  {dashboardData?.top_staff?.length || 0} learners
                </span>
              </div>
              <div className="table-container">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Avg Score</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.top_staff?.slice(0, 5).map((staff, idx) => (
                      <tr key={staff.id || idx}>
                        <td>
                          <div className="learner-cell">
                            <span className="learner-rank">#{idx + 1}</span>
                            <span className="learner-name">
                              {staff.full_name ||
                                `${staff.firstname || ""} ${staff.lastname || ""}`.trim() ||
                                "Unknown"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <strong>
                            {staff.tests_avg_percentile
                              ? `${parseFloat(staff.tests_avg_percentile).toFixed(1)}%`
                              : "—"}
                          </strong>
                        </td>
                        <td>{staff.phone_number || "—"}</td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan="3" className="no-data">
                          No learners data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courses Tab */}
      {activeTab === "courses" && (
        <div className="analytics-content">
          <div className="section-header">
            <h2>Course Analytics</h2>
            <div className="section-stats">
              <span>Total: {metrics.totalCourses}</span>
              <span>Published: {metrics.publishedCourses}</span>
            </div>
          </div>

          <div className="chart-card full-width">
            <div className="chart-header">
              <h3>Course Enrollments Overview</h3>
            </div>
            <div className="chart-container">
              {topCoursesData ? (
                <Bar data={topCoursesData} options={chartOptions} />
              ) : (
                <div className="chart-placeholder">No data available</div>
              )}
            </div>
          </div>

          <div className="table-card full-width">
            <div className="table-header">
              <h3>All Courses</h3>
            </div>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Enrollments</th>
                    <th>Completed</th>
                    <th>Avg Score</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.length > 0 ? (
                    courses.map((course) => {
                      // Merge course data with dashboard data if available for additional metrics
                      // const dashboardCourse =
                      //   dashboardData?.course_status?.find(
                      //     (c) => c.id === course.id,
                      //   );
                     
                      return (
                        <tr key={course.id}>
                          <td>{course.title || "Untitled"}</td>
                          <td>
                            <strong>{course.learners_count || 0}</strong>
                          </td>
                          <td className="success-text">
                            {course.completed || 0}
                          </td>
                          <td>
                            {course.tests_avg_percentile
                              ? `${parseFloat(course.tests_avg_percentile).toFixed(1)}%`
                              : "—"}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${course.is_published ? "status-published" : "status-draft"}`}
                            >
                              {course.is_published ? "Published" : "Draft"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">
                        No courses data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Learners Tab */}
      {activeTab === "learners" && (
        <div className="analytics-content">
          <div className="section-header">
            <h2>Learner Analytics</h2>
            <div className="section-stats">
              <span>Total: {learners.length}</span>
              <span>Avg Score: {metrics.averageScore}%</span>
            </div>
          </div>

          <div className="chart-card full-width">
            <div className="chart-header">
              <h3>Top Performers Performance</h3>
            </div>
            <div className="chart-container">
              {topStaffData ? (
                <Line data={topStaffData} options={chartOptions} />
              ) : (
                <div className="chart-placeholder">No data available</div>
              )}
            </div>
          </div>

          {/* Top Learners */}
          <div className="tables-grid">
            <div className="table-card">
              <div className="table-header">
                <h3>Top Performing Learners</h3>
                <span className="badge">
                  {dashboardData?.top_staff?.length || 0} learners
                </span>
              </div>
              <div className="table-container">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Avg Score</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.top_staff?.slice(0, 5).map((staff, idx) => (
                      <tr key={staff.id || idx}>
                        <td>
                          <div className="learner-cell">
                            <span className="learner-rank">#{idx + 1}</span>
                            <span className="learner-name">
                              {staff.full_name ||
                                `${staff.firstname || ""} ${staff.lastname || ""}`.trim() ||
                                "Unknown"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <strong>
                            {staff.tests_avg_percentile
                              ? `${parseFloat(staff.tests_avg_percentile).toFixed(1)}%`
                              : "—"}
                          </strong>
                        </td>
                        <td>{staff.phone_number || "—"}</td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan="3" className="no-data">
                          No learners data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* <div className="table-card full-width">
            <div className="table-header">
              <h3>All Learners</h3>
              <span className="badge">{learners.length} learners</span>
            </div>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Phone</th>
                    <th>Avg Score</th>
                    <th>Facility</th>
                  </tr>
                </thead>
                <tbody>
                  {learners.length > 0 ? (
                    learners
                      .slice(
                        (learnersPage - 1) * learnersPerPage,
                        learnersPage * learnersPerPage,
                      )
                      .map((staff) => (
                        <tr key={staff.id}>
                          <td>
                            <strong>
                              {staff.full_name ||
                                `${staff.firstname || ""} ${staff.lastname || ""}`.trim() ||
                                "Unknown"}
                            </strong>
                          </td>
                          <td>{staff.phone_number || "—"}</td>
                          <td>
                            <strong className="score-text">
                              {staff.tests_avg_percentile
                                ? `${parseFloat(staff.tests_avg_percentile).toFixed(1)}%`
                                : "—"}
                            </strong>
                          </td>
                          <td>
                            {staff.facility?.name || staff.facility_name || "—"}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No learners data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {learners.length > learnersPerPage && (
              <Pagination
                currentPage={learnersPage}
                totalPages={Math.ceil(learners.length / learnersPerPage)}
                onPageChange={setLearnersPage}
                isLoading={refreshing}
              />
            )}
          </div> */}


        </div>
      )}

      {activeTab === "custom" && (
        <div className="analytics-content">
          <div className="section-header">
            <h2>Custom Query Builder</h2>
            <div className="section-stats">
              <span>Build and run dynamic chart queries</span>
            </div>
          </div>

          <div className="query-builder-card">
            <div className="query-builder-grid">
              <label className="query-field">
                <span>Chart Type</span>
                <select
                  value={queryConfig.chartType}
                  onChange={(e) =>
                    setQueryConfig((prev) => ({
                      ...prev,
                      chartType: e.target.value,
                    }))
                  }
                  disabled={querySchemaLoading}
                >
                  <option value="bar">Bar</option>
                  <option value="pie">Pie</option>
                </select>
              </label>

              <label className="query-field">
                <span>Source Table</span>
                <select
                  value={queryConfig.sourceTable}
                  onChange={(e) => handleSourceTableChange(e.target.value)}
                  disabled={querySchemaLoading}
                >
                  {Object.keys(querySchema?.tables || {}).map((table) => (
                    <option key={table} value={table}>
                      {table}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>X Axis Table</span>
                <select
                  value={queryConfig.xAxisTable}
                  onChange={(e) => handleXAxisTableChange(e.target.value)}
                  disabled={querySchemaLoading}
                >
                  {Object.keys(querySchema?.tables || {}).map((table) => (
                    <option key={table} value={table}>
                      {table}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>X Axis Column</span>
                <select
                  value={queryConfig.xAxisColumn}
                  onChange={(e) =>
                    setQueryConfig((prev) => ({
                      ...prev,
                      xAxisColumn: e.target.value,
                    }))
                  }
                  disabled={querySchemaLoading}
                >
                  {getColumnsForTable(queryConfig.xAxisTable).map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>Metric Function</span>
                <select
                  value={queryConfig.metricFunc}
                  onChange={(e) =>
                    setQueryConfig((prev) => ({
                      ...prev,
                      metricFunc: e.target.value,
                    }))
                  }
                  disabled={querySchemaLoading}
                >
                  {(querySchema?.aggregates || ["count"]).map((fn) => (
                    <option key={fn} value={fn}>
                      {fn.toUpperCase()}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>Metric Table</span>
                <select
                  value={queryConfig.metricTable}
                  onChange={(e) => handleMetricTableChange(e.target.value)}
                  disabled={querySchemaLoading}
                >
                  {Object.keys(querySchema?.tables || {}).map((table) => (
                    <option key={table} value={table}>
                      {table}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>Metric Column</span>
                <select
                  value={queryConfig.metricColumn}
                  onChange={(e) =>
                    setQueryConfig((prev) => ({
                      ...prev,
                      metricColumn: e.target.value,
                    }))
                  }
                  disabled={querySchemaLoading}
                >
                  {getColumnsForTable(queryConfig.metricTable).map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </label>

              <label className="query-field">
                <span>Limit</span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={queryConfig.limit}
                  onChange={(e) =>
                    setQueryConfig((prev) => ({
                      ...prev,
                      limit: e.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <div className="join-builder">
              <div className="join-builder-header">
                <h4>Joins (Optional)</h4>
                <button className="secondary-btn" onClick={addJoinRow}>
                  Add Join
                </button>
              </div>

              {(queryConfig.joins || []).length === 0 && (
                <p className="query-note">No joins added.</p>
              )}

              {(queryConfig.joins || []).map((join, index) => (
                <div key={`${join.fromTable}-${index}`} className="join-row">
                  <select
                    value={join.fromTable}
                    onChange={(e) =>
                      updateJoinRow(index, "fromTable", e.target.value)
                    }
                  >
                    {Object.keys(querySchema?.joins || {}).map((table) => (
                      <option key={table} value={table}>
                        {table}
                      </option>
                    ))}
                  </select>

                  <select
                    value={join.toTable}
                    onChange={(e) =>
                      updateJoinRow(index, "toTable", e.target.value)
                    }
                  >
                    {getJoinTargets(join.fromTable).map((table) => (
                      <option key={table} value={table}>
                        {table}
                      </option>
                    ))}
                  </select>

                  <button
                    className="danger-btn"
                    onClick={() => removeJoinRow(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="query-actions">
              <button
                className="refresh-btn"
                onClick={handleRunCustomQuery}
                disabled={querySchemaLoading || customQueryLoading}
              >
                {customQueryLoading ? "Running Query..." : "Run Query"}
              </button>

              <button className="secondary-btn" onClick={loadGenderExample}>
                Load Staff Gender Example
              </button>
            </div>

            {customQueryError && (
              <div className="query-error">{customQueryError}</div>
            )}
          </div>

          <div className="chart-card full-width">
            <div className="chart-header">
              <h3>Query Result</h3>
              <p>
                {customQueryResult?.meta?.records || 0} records
                {customQueryResult?.meta?.xAxis
                  ? ` • X axis: ${customQueryResult.meta.xAxis}`
                  : ""}
              </p>
            </div>
            <div className="chart-container">
              {customChartData ? (
                queryConfig.chartType === "pie" ? (
                  <Doughnut data={customChartData} options={doughnutOptions} />
                ) : (
                  <Bar data={customChartData} options={chartOptions} />
                )
              ) : (
                <div className="chart-placeholder">
                  Run a query to render your chart.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
